import { useState, useMemo } from "react";
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import { z } from "zod";
import {
  UploadSimple,
  Tag,
  Warning,
  ArrowClockwise,
} from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Drawer,
  DrawerContent,
  DrawerDescription,
  DrawerHeader,
  DrawerTitle,
} from "@/components/ui/drawer";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
} from "@/components/ui/form";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Badge } from "@/components/ui/badge";
import { Alert, AlertDescription } from "@/components/ui/alert";
import {
  useTemplateTags,
  useCreateTemplate,
} from "@/lib/queries/user/useTemplates";
import { useTemplateFileUpload } from "@/lib/queries/tools/useFileUpload";
import { useToast } from "@/hooks/use-toast";
import { useIsMobile } from "@/hooks/use-mobile";
import { useTranslation } from "react-i18next";

const templateUploadSchema = z.object({
  file: z
    .any()
    .refine((file) => file instanceof File, "validation.file.required")
    .refine((file) => file.size <= 10 * 1024 * 1024, "validation.file.size")
    .refine(
      (file) =>
        [
          "application/pdf",
          "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
          "application/msword",
          "text/plain",
        ].includes(file.type),
      "validation.file.type"
    ),
  name: z.string().optional(),
  tags: z.array(z.string()).min(1, "validation.tags.required"),
});

function TemplateUploadForm({ onSuccess }: { onSuccess: () => void }) {
  const { t } = useTranslation(["templates", "communicationGenerator"]);
  const [selectedTags, setSelectedTags] = useState<string[]>([]); // Now stores "category|subcategory|tag" format
  const [dragActive, setDragActive] = useState(false);
  const [activeCategory, setActiveCategory] = useState<string>(""); // Will be set to first category
  const { toast } = useToast();

  const {
    data: availableTags,
    isLoading: tagsLoading,
    isError: tagsError,
    error: tagsErrorDetails,
    refetch: refetchTags,
  } = useTemplateTags();
  const { mutate: uploadFile, isPending: uploading } = useTemplateFileUpload();
  const { mutate: createTemplate, isPending: creating } = useCreateTemplate();

  // Memoize the organized tag structure
  const organizedTags = useMemo(() => {
    if (!availableTags?.results) return { categories: [], tagsByCategory: {} };

    const tagsByCategory: Record<
      string,
      Record<
        string,
        {
          tagText: string;
          fullId: string;
          general: boolean;
          key: string;
          subcategory: string;
        }[]
      >
    > = {};
    const categories = new Set<string>();

    availableTags.results.forEach(
      ({ category, subcategory, tagText, general, key }) => {
        categories.add(category);

        if (!tagsByCategory[category]) {
          tagsByCategory[category] = {};
        }

        const subcat = subcategory || "General";
        if (!tagsByCategory[category][subcat]) {
          tagsByCategory[category][subcat] = [];
        }

        // Create unique identifier: category|subcategory|tag|general
        const fullId = `${category}|${subcat}|${tagText}|${general}`;
        tagsByCategory[category][subcat].push({
          tagText: tagText,
          fullId,
          general,
          key,
          subcategory: subcat,
        });
      }
    );

    return {
      categories: Array.from(categories).sort(),
      tagsByCategory,
    };
  }, [availableTags?.results]);

  // Set initial active category when data loads
  useMemo(() => {
    if (organizedTags.categories.length > 0 && !activeCategory) {
      setActiveCategory(organizedTags.categories[0]);
    }
  }, [organizedTags.categories, activeCategory]);

  // Memoize selected tags by category for better UX
  const selectedTagsByCategory = useMemo(() => {
    const byCategory: Record<string, number> = {};
    selectedTags.forEach((fullId) => {
      // Extract category from fullId format: "category|subcategory|tag|general"
      const [category] = fullId.split("|");
      if (category) {
        byCategory[category] = (byCategory[category] || 0) + 1;
      }
    });
    return byCategory;
  }, [selectedTags, organizedTags.tagsByCategory]);

  const form = useForm<z.infer<typeof templateUploadSchema>>({
    resolver: zodResolver(templateUploadSchema),
    defaultValues: {
      name: "",
      tags: [],
    },
  });

  // Custom error message translation
  const getTranslatedError = (error: any) => {
    if (!error?.message) return undefined;

    // Check if the error message is a translation key
    if (error.message.startsWith("validation.")) {
      return t(error.message);
    }

    return error.message;
  };

  const handleDrag = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === "dragenter" || e.type === "dragover") {
      setDragActive(true);
    } else if (e.type === "dragleave") {
      setDragActive(false);
    }
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);

    const files = Array.from(e.dataTransfer.files);
    if (files.length > 0) {
      form.setValue("file", files[0]);
    }
  };

  const toggleTag = (fullId: string) => {
    const newTags = selectedTags.includes(fullId)
      ? selectedTags.filter((t) => t !== fullId)
      : [...selectedTags, fullId];

    setSelectedTags(newTags);
    form.setValue("tags", newTags);
  };

  const onSubmit = async (data: z.infer<typeof templateUploadSchema>) => {
    try {
      // Calculate file hash (simple approach for demo)
      const arrayBuffer = await data.file.arrayBuffer();
      const hashBuffer = await crypto.subtle.digest("SHA-256", arrayBuffer);
      const hash = Array.from(new Uint8Array(hashBuffer))
        .map((b) => b.toString(16).padStart(2, "0"))
        .join("");

      // Upload file
      uploadFile(
        {
          filename: data.file.name,
          hash,
          binary: arrayBuffer,
        },
        {
          onSuccess: (uploadResponse) => {
            createTemplate(
              {
                id: uploadResponse.filename.split(".")[0],
                name: data.name?.trim() || data.file.name,
                fileHash: hash,
                tags: selectedTags.map((fullId) => {
                  const [category, subcategory, tagText, general] =
                    fullId.split("|");
                  return {
                    category,
                    subcategory:
                      subcategory === "General" ? undefined : subcategory,
                    tagText,
                    general: general === "true",
                  };
                }),
              },
              {
                onSuccess: () => {
                  toast({
                    title: t("uploadModal.success.title"),
                    description: t("uploadModal.success.description"),
                  });
                  form.reset();
                  setSelectedTags([]);
                  setActiveCategory(organizedTags.categories[0] || "");
                  onSuccess();
                },
                onError: (error) => {
                  toast({
                    title: t("uploadModal.error.save.title"),
                    description: error.message,
                    variant: "destructive",
                  });
                },
              }
            );
          },
          onError: (error) => {
            toast({
              title: t("uploadModal.error.upload.title"),
              description: error.message,
              variant: "destructive",
            });
          },
        }
      );
    } catch (error) {
      toast({
        title: t("uploadModal.error.upload.title"),
        description: t("uploadModal.error.upload.description"),
        variant: "destructive",
      });
    }
  };

  if (tagsLoading) {
    return (
      <div className="space-y-6">
        <div className="p-4 border border-gray-200 rounded-lg bg-gray-50">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-8 h-8 bg-gray-200 rounded-lg animate-pulse" />
            <div className="w-24 h-4 bg-gray-200 rounded animate-pulse" />
          </div>
          <div className="space-y-3">
            <div className="w-full h-32 bg-gray-200 rounded-lg animate-pulse" />
          </div>
        </div>
        <div className="p-4 bg-white border border-gray-200 rounded-lg">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-8 h-8 bg-gray-200 rounded-lg animate-pulse" />
            <div className="w-32 h-4 bg-gray-200 rounded animate-pulse" />
          </div>
          <div className="space-y-4">
            <div className="flex gap-2">
              {Array.from({ length: 3 }).map((_, i) => (
                <div
                  key={i}
                  className={`${i === 0 ? "w-20" : i === 1 ? "w-24" : "w-16"} h-8 bg-gray-200 rounded animate-pulse`}
                />
              ))}
            </div>
            <div className="space-y-3">
              <div className="w-20 h-4 bg-gray-200 rounded animate-pulse" />
              <div className="grid grid-cols-2 gap-2 sm:grid-cols-3">
                {Array.from({ length: 6 }).map((_, i) => (
                  <div
                    key={i}
                    className="w-full h-10 bg-gray-200 rounded animate-pulse"
                  />
                ))}
              </div>
            </div>
          </div>
        </div>
        <div className="flex justify-end pt-4">
          <div className="w-32 h-10 bg-gray-200 rounded animate-pulse" />
        </div>
      </div>
    );
  }

  if (tagsError) {
    const is404 =
      tagsErrorDetails?.message?.includes("404") ||
      tagsErrorDetails?.message?.includes("Not Found");

    return (
      <div className="space-y-6">
        <div>
          <Alert variant="destructive">
            <Warning className="w-4 h-4" />
            <AlertDescription className="space-y-3">
              <p>
                {is404
                  ? t("uploadModal.error.tags404")
                  : `${t("uploadModal.error.tags.prefix")} ${tagsErrorDetails?.message || t("uploadModal.error.tags.unknown")}`}
              </p>
              <div>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => refetchTags()}
                  className="w-full"
                >
                  <ArrowClockwise className="w-4 h-4 mr-2" />
                  {t("uploadModal.error.tryAgain")}
                </Button>
              </div>
            </AlertDescription>
          </Alert>
        </div>
      </div>
    );
  }

  const hasNoTags = organizedTags.categories.length === 0;

  if (hasNoTags) {
    return (
      <div className="space-y-6">
        <div>
          <Alert>
            <Warning className="w-4 h-4" />
            <AlertDescription className="space-y-3">
              <p>{t("uploadModal.error.noTags")}</p>
              <div>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => refetchTags()}
                  className="w-full"
                >
                  <ArrowClockwise className="w-4 h-4 mr-2" />
                  {t("uploadModal.error.refreshTags")}
                </Button>
              </div>
            </AlertDescription>
          </Alert>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <Form {...form}>
        <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
          {/* File Upload Section */}
          <div className="p-4 border border-gray-200 rounded-lg bg-gray-50">
            <div className="flex items-center gap-3 mb-4">
              <div className="flex items-center justify-center w-8 h-8 rounded-lg bg-primary/10">
                <UploadSimple className="w-4 h-4 text-primary" />
              </div>
              <h3 className="font-medium text-gray-900">
                {t("uploadModal.fileSection.title")}
              </h3>
            </div>
            <FormField
              name="file"
              control={form.control}
              render={({ field: { onChange, value, ...field } }) => (
                <FormItem>
                  <FormControl>
                    <div
                      className={`relative border-2 border-dashed rounded-lg p-6 transition-colors ${
                        dragActive
                          ? "border-primary bg-primary/5"
                          : "border-gray-300 bg-white hover:border-primary/50"
                      }`}
                      onDragEnter={handleDrag}
                      onDragLeave={handleDrag}
                      onDragOver={handleDrag}
                      onDrop={handleDrop}
                    >
                      <input
                        type="file"
                        className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
                        accept=".pdf,.doc,.docx,.txt"
                        onChange={(e) => {
                          const file = e.target.files?.[0];
                          if (file) onChange(file);
                        }}
                        {...field}
                      />
                      <div className="text-center">
                        <div>
                          <UploadSimple className="w-8 h-8 mx-auto mb-3 text-gray-400" />
                        </div>
                        <p className="mb-1 font-medium text-gray-900">
                          {value?.name || t("uploadModal.fileSection.dropText")}
                        </p>
                        <p className="text-sm text-gray-500">
                          {t("uploadModal.fileSection.browseText")}
                        </p>
                      </div>
                    </div>
                  </FormControl>
                  <div className="text-sm text-red-600">
                    {getTranslatedError(form.formState.errors.file)}
                  </div>
                </FormItem>
              )}
            />
          </div>

          {/* Template Name Section */}
          <div className="p-4 border border-gray-200 rounded-lg bg-gray-50">
            <div className="flex items-center gap-3 mb-4">
              <div className="flex items-center justify-center w-8 h-8 rounded-lg bg-primary/10">
                <Tag className="w-4 h-4 text-primary" />
              </div>
              <h3 className="font-medium text-gray-900">
                {t("uploadModal.nameSection.title")}
              </h3>
            </div>
            <FormField
              name="name"
              control={form.control}
              render={({ field }) => (
                <FormItem>
                  <FormLabel className="text-sm font-medium text-gray-700">
                    {t("uploadModal.nameSection.label")}
                  </FormLabel>
                  <FormControl>
                    <input
                      type="text"
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
                      placeholder={
                        form.watch("file")?.name ||
                        t("uploadModal.nameSection.placeholder")
                      }
                      {...field}
                    />
                  </FormControl>
                  <p className="mt-1 text-xs text-gray-500">
                    {t("uploadModal.nameSection.help")}
                  </p>
                  <div className="text-sm text-red-600">
                    {getTranslatedError(form.formState.errors.name)}
                  </div>
                </FormItem>
              )}
            />
          </div>

          <div className="p-4 bg-white border border-gray-200 rounded-lg">
            <div className="flex items-center gap-3 mb-4">
              <div className="flex items-center justify-center w-8 h-8 rounded-lg bg-primary/10">
                <Tag className="w-4 h-4 text-primary" />
              </div>
              <h3 className="font-medium text-gray-900">
                {t("uploadModal.tagsSection.title")}
              </h3>
            </div>

            <FormField
              name="tags"
              control={form.control}
              render={() => (
                <FormItem>
                  <FormLabel className="text-sm font-medium text-gray-700">
                    {t("uploadModal.tagsSection.label")}
                    {selectedTags.length > 0 && (
                      <span className="ml-2 font-normal text-primary">
                        {t("uploadModal.tagsSection.selected", {
                          count: selectedTags.length,
                        })}
                      </span>
                    )}
                  </FormLabel>
                  <FormControl>
                    <div className="mt-3">
                      <Tabs
                        value={activeCategory}
                        onValueChange={setActiveCategory}
                        className="w-full"
                      >
                        <TabsList
                          className={`grid w-full ${organizedTags.categories.length > 3 ? "grid-cols-2 sm:grid-cols-3 lg:grid-cols-4" : `grid-cols-${organizedTags.categories.length}`}`}
                        >
                          {organizedTags.categories.map((category) => (
                            <TabsTrigger
                              key={category}
                              value={category}
                              className="flex items-center gap-1 px-2 text-xs"
                            >
                              <span className="truncate">
                                {t(category, {
                                  ns: "communicationGenerator",
                                })}
                              </span>
                              {selectedTagsByCategory[category] > 0 && (
                                <Badge
                                  variant="secondary"
                                  className="text-xs px-1 py-0 h-4 min-w-[16px]"
                                >
                                  {selectedTagsByCategory[category]}
                                </Badge>
                              )}
                            </TabsTrigger>
                          ))}
                        </TabsList>
                        {organizedTags.categories.map((category) => (
                          <TabsContent
                            key={category}
                            value={category}
                            className="mt-4"
                          >
                            <div className="space-y-4">
                              {Object.entries(
                                organizedTags.tagsByCategory[category] || {}
                              ).map(([subcategory, tagObjects]) => (
                                <div key={subcategory} className="space-y-2">
                                  <h4 className="text-sm font-medium text-gray-800">
                                    {subcategory}
                                  </h4>
                                  <div className="grid grid-cols-2 gap-2 sm:grid-cols-3">
                                    {tagObjects.map(
                                      ({
                                        fullId,
                                        general,
                                        key,
                                        subcategory,
                                      }) => {
                                        const isResource =
                                          fullId.split("|")[0] === "resource";
                                        console.log(
                                          `commType.${subcategory.toLowerCase()}.${key === "all" ? (isResource ? "allResources" : "all") : key}`
                                        );
                                        return (
                                          <label
                                            key={fullId}
                                            className={`flex items-center p-2 border rounded-lg cursor-pointer transition-colors text-sm ${
                                              selectedTags.includes(fullId)
                                                ? "border-primary bg-primary/5 text-primary"
                                                : "border-gray-200 hover:border-gray-300 text-gray-700"
                                            }`}
                                          >
                                            <input
                                              type="checkbox"
                                              checked={selectedTags.includes(
                                                fullId
                                              )}
                                              onChange={() => toggleTag(fullId)}
                                              className="w-4 h-4 mr-2 border-gray-300 rounded text-primary focus:ring-primary"
                                            />
                                            <span className="flex-1 truncate">
                                              {t(
                                                `commType.${subcategory.toLowerCase()}.${key === "all" ? (isResource ? "allResources" : "all") : key}`,
                                                {
                                                  ns: "communicationGenerator",
                                                }
                                              )}
                                            </span>
                                            {general && (
                                              <span
                                                className="ml-1 text-xs text-purple-600"
                                                title={t(
                                                  "uploadModal.tagsSection.generalTooltip"
                                                )}
                                              >
                                                ★
                                              </span>
                                            )}
                                          </label>
                                        );
                                      }
                                    )}
                                  </div>
                                </div>
                              ))}
                            </div>
                          </TabsContent>
                        ))}
                      </Tabs>
                    </div>
                  </FormControl>
                  {selectedTags.length > 0 && (
                    <div className="mt-4">
                      <p className="mb-2 text-sm font-medium text-gray-700">
                        Selected Tags:
                      </p>
                      <div className="flex flex-wrap gap-2">
                        {selectedTags.map((fullId) => {
                          // Parse fullId to get display name: "category|subcategory|tag|general"
                          const [category, subcategory, tag, general] =
                            fullId.split("|");
                          const displayName =
                            subcategory && subcategory !== "General"
                              ? `${tag} (${category} → ${subcategory})`
                              : `${tag} (${category})`;

                          return (
                            <div key={fullId}>
                              <Badge
                                variant="secondary"
                                className="text-gray-700 bg-gray-100"
                              >
                                {displayName}
                                {general === "true" && (
                                  <span className="ml-1 text-xs text-purple-600">
                                    ★
                                  </span>
                                )}
                                <button
                                  type="button"
                                  onClick={() => toggleTag(fullId)}
                                  className="ml-1 rounded-full hover:bg-gray-200"
                                >
                                  ×
                                </button>
                              </Badge>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  )}
                  <div className="text-sm text-red-600">
                    {getTranslatedError(form.formState.errors.tags)}
                  </div>
                </FormItem>
              )}
            />
          </div>
          <div className="flex justify-end pt-4">
            <Button
              type="submit"
              disabled={uploading || creating}
              className="px-6"
            >
              {uploading || creating ? (
                <div className="flex items-center">
                  <div className="w-4 h-4 mr-2 border-2 border-white rounded-full border-t-transparent animate-spin" />
                  {t("uploadModal.submitButton.uploading")}
                </div>
              ) : (
                <div className="flex items-center">
                  <UploadSimple className="w-4 h-4 mr-2" />
                  {t("uploadModal.submitButton.upload")}
                </div>
              )}
            </Button>
          </div>
        </form>
      </Form>
    </div>
  );
}

interface TemplateUploadModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSuccess: () => void;
}

export function TemplateUploadModal({
  open,
  onOpenChange,
  onSuccess,
}: TemplateUploadModalProps) {
  const { t } = useTranslation("templates");
  const isMobile = useIsMobile();

  const handleSuccess = () => {
    onSuccess();
    onOpenChange(false);
  };

  if (isMobile) {
    return (
      <Drawer open={open} onOpenChange={onOpenChange}>
        <DrawerContent className="max-h-[85vh]">
          <DrawerHeader>
            <DrawerTitle>{t("uploadModal.title")}</DrawerTitle>
            <DrawerDescription>
              {t("uploadModal.description")}
            </DrawerDescription>
          </DrawerHeader>
          <div className="px-4 pb-4 overflow-auto">
            <TemplateUploadForm onSuccess={handleSuccess} />
          </div>
        </DrawerContent>
      </Drawer>
    );
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[85vh] overflow-auto max-w-4xl">
        <DialogHeader>
          <DialogTitle>{t("uploadModal.title")}</DialogTitle>
          <DialogDescription>{t("uploadModal.description")}</DialogDescription>
        </DialogHeader>
        <TemplateUploadForm onSuccess={handleSuccess} />
      </DialogContent>
    </Dialog>
  );
}

export default TemplateUploadModal;
